/**
 * AdapterStateManager.js
 * 
 * State machine manager specifically for RendererAdapter
 * This provides the state management that RendererAdapter expects
 */

// Define adapter states
export const AdapterState = {
  UNINITIALIZED: 'uninitialized',
  INITIALIZING: 'initializing',
  INITIALIZED: 'initialized',
  READY: 'ready',
  RENDERING: 'rendering',
  REFRESHING: 'refreshing',
  DESTROYING: 'destroying',
  DESTROYED: 'destroyed',
  ERROR: 'error'
};

/**
 * State manager for adapter components with state machine logic
 */
export class AdapterStateManager {
  constructor(logger = null) {
    this.logger = logger;
    this.currentState = AdapterState.UNINITIALIZED;
    this.stateHistory = [];
    this.metadata = {};
    
    // Define valid state transitions
    this.validTransitions = {
      [AdapterState.UNINITIALIZED]: [AdapterState.INITIALIZING],
      [AdapterState.INITIALIZING]: [AdapterState.INITIALIZED, AdapterState.ERROR],
      [AdapterState.INITIALIZED]: [AdapterState.READY, AdapterState.DESTROYING, AdapterState.ERROR],
      [AdapterState.READY]: [AdapterState.RENDERING, AdapterState.REFRESHING, AdapterState.DESTROYING, AdapterState.ERROR],
      [AdapterState.RENDERING]: [AdapterState.READY, AdapterState.ERROR],
      [AdapterState.REFRESHING]: [AdapterState.READY, AdapterState.ERROR],
      [AdapterState.DESTROYING]: [AdapterState.DESTROYED],
      [AdapterState.DESTROYED]: [], // Terminal state
      [AdapterState.ERROR]: [AdapterState.INITIALIZING, AdapterState.DESTROYING] // Can retry or destroy
    };
  }

  /**
   * Get current state
   */
  getCurrentState() {
    return this.currentState;
  }

  /**
   * Check if adapter is in a specific state
   */
  isInState(state) {
    return this.currentState === state;
  }

  /**
   * Check if adapter is ready for operations
   */
  isReady() {
    return this.currentState === AdapterState.READY;
  }

  /**
   * Check if adapter is operational (can perform actions)
   */
  isOperational() {
    const operationalStates = [
      AdapterState.INITIALIZED,
      AdapterState.READY,
      AdapterState.RENDERING,
      AdapterState.REFRESHING
    ];
    return operationalStates.includes(this.currentState);
  }

  /**
   * Check if transition to a state is valid
   */
  canTransitionTo(newState) {
    const allowedStates = this.validTransitions[this.currentState] || [];
    return allowedStates.includes(newState);
  }

  /**
   * Transition to a new state
   */
  transitionTo(newState, metadata = {}) {
    if (!this.canTransitionTo(newState)) {
      const error = `Invalid state transition: ${this.currentState} -> ${newState}`;
      if (this.logger) {
        this.logger.error(error);
      }
      throw new Error(error);
    }

    const previousState = this.currentState;
    this.currentState = newState;
    this.metadata = { ...this.metadata, ...metadata };
    
    // Add to history
    this.stateHistory.push({
      from: previousState,
      to: newState,
      timestamp: Date.now(),
      metadata: { ...metadata }
    });

    // Keep history reasonable size
    if (this.stateHistory.length > 50) {
      this.stateHistory = this.stateHistory.slice(-25);
    }

    if (this.logger) {
      this.logger.debug(`State transition: ${previousState} -> ${newState}`, metadata);
    }
  }

  /**
   * Force state (for error recovery)
   */
  forceState(newState, metadata = {}) {
    if (this.logger) {
      this.logger.warn(`Forcing state transition: ${this.currentState} -> ${newState}`);
    }
    
    const previousState = this.currentState;
    this.currentState = newState;
    this.metadata = { ...this.metadata, ...metadata };
    
    this.stateHistory.push({
      from: previousState,
      to: newState,
      timestamp: Date.now(),
      metadata: { ...metadata },
      forced: true
    });
  }

  /**
   * Get state history
   */
  getStateHistory() {
    return [...this.stateHistory];
  }

  /**
   * Get current metadata
   */
  getMetadata() {
    return { ...this.metadata };
  }

  /**
   * Reset state manager
   */
  reset() {
    this.currentState = AdapterState.UNINITIALIZED;
    this.stateHistory = [];
    this.metadata = {};
    
    if (this.logger) {
      this.logger.debug('State manager reset');
    }
  }

  /**
   * Get status information
   */
  getStatus() {
    return {
      currentState: this.currentState,
      isReady: this.isReady(),
      isOperational: this.isOperational(),
      metadata: this.getMetadata(),
      historyLength: this.stateHistory.length,
      lastTransition: this.stateHistory[this.stateHistory.length - 1] || null
    };
  }
}

// For backward compatibility, export as StateManager too
export const StateManager = AdapterStateManager;